import { useCallback, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useGetAllAddonsQuery } from "../store/apis/plans.api";

/**
 * Strict addon access checker.
 *
 * The previous implementation walked the user object recursively looking
 * for any token that matched the addon slug. That produced both false
 * positives (granting features the user didn't pay for, if a similarly-named
 * key showed up anywhere in the tree) and false negatives (missing ones
 * they actually purchased, if the field name didn't match the heuristic).
 *
 * This version uses ONE source of truth: user.selectedAddOns. The backend
 * stores those as ObjectIds referencing crm_addons. We resolve the addon
 * key the caller asked about (e.g. "agentSocialMediaSuite") to its real
 * _id via the addon directory, then check membership.
 */

const slugify = (input) => {
  if (typeof input !== "string") {
    if (input == null) return "";
    return slugify(String(input));
  }
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

/**
 * Build a lookup index from the addon directory:
 *   key (e.g. "agentSocialMediaSuite") → _id (string)
 *   slug (e.g. "agent-social-media-suite") → _id (string)
 *   _id → _id (passthrough for direct lookups)
 */
const buildAddonIndex = (addonsResponse) => {
  const addons = addonsResponse?.data || addonsResponse;
  const byKey = new Map();
  if (Array.isArray(addons)) {
    for (const addon of addons) {
      if (!addon?._id) continue;
      const id = String(addon._id);
      if (addon.key) byKey.set(addon.key, id);
      if (addon.addon_name) byKey.set(slugify(addon.addon_name), id);
      // Also map _id → _id so callers passing the raw _id work.
      byKey.set(id, id);
      // And the slugified key (e.g. "agent-social-media-suite")
      if (addon.key) byKey.set(slugify(addon.key), id);
    }
  }
  return byKey;
};

/**
 * Pull the set of addon _ids the user actually owns. Sources, in priority
 * order:
 *   1. user.selectedAddOns (the canonical field — backend writes this on
 *      successful subscription)
 *   2. user.parentUser.selectedAddOns (for invited seats / VAs — they
 *      inherit their owner's addons)
 *
 * Returns a Set of _id strings. If the user has no addons, returns an
 * empty Set (caller will deny access).
 */
const collectOwnedAddonIds = (user) => {
  const owned = new Set();
  if (!user) return owned;

  const sources = [user.selectedAddOns, user.parentUser?.selectedAddOns];
  for (const source of sources) {
    if (!Array.isArray(source)) continue;
    for (const entry of source) {
      if (!entry) continue;
      if (typeof entry === "string") {
        owned.add(entry);
      } else if (typeof entry === "object") {
        if (entry._id) owned.add(String(entry._id));
        else if (entry.id) owned.add(String(entry.id));
      }
    }
  }
  return owned;
};

export const useAddonAccessChecker = () => {
  const { user, loading: userLoading } = useAuth();
  const { data: addonsResponse, isLoading: addonsLoading } = useGetAllAddonsQuery(
    undefined,
    { refetchOnMountOrArgChange: false }
  );

  const addonIndex = useMemo(() => buildAddonIndex(addonsResponse), [addonsResponse]);
  const ownedIds = useMemo(() => collectOwnedAddonIds(user), [user]);

  const canAccess = useCallback(
    (addonKey) => {
      if (!user) return false;

      // Admins always pass — they need to be able to QA every feature
      if (user.role === "admin") return true;

      if (!addonKey) return false;

      // Resolve the requested key to a canonical _id via the directory
      const requestedId =
        addonIndex.get(addonKey) ||
        addonIndex.get(slugify(addonKey)) ||
        addonKey; // last resort: maybe addonKey IS the _id

      return ownedIds.has(String(requestedId));
    },
    [user, ownedIds, addonIndex]
  );

  return {
    canAccess,
    loading: userLoading || addonsLoading,
    user,
  };
};

export const useAddonAccess = (addonKey, options) => {
  const { canAccess, loading, user } = useAddonAccessChecker();
  const hasAccess = useMemo(() => canAccess(addonKey, options), [addonKey, options, canAccess]);
  return { hasAccess, loading, user };
};

export { slugify };
